import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Wat zijn algemene voorwaarden?',
    answer: 'Algemene voorwaarden (AV) zijn een set standaardbepalingen die van toepassing zijn op alle overeenkomsten die je als freelancer sluit. Ze regelen zaken zoals betalingstermijnen, aansprakelijkheid, klachtenafhandeling en intellectueel eigendom. Door AV te gebruiken hoef je niet bij elke opdracht opnieuw over deze basisafspraken te onderhandelen.',
  },
  {
    question: 'Zijn algemene voorwaarden verplicht voor freelancers?',
    answer: 'Algemene voorwaarden zijn niet wettelijk verplicht, maar ze zijn sterk aanbevolen voor elke freelancer. Ze beschermen je bij geschillen, beperken je aansprakelijkheid en maken duidelijk wat klanten van je kunnen verwachten. Zonder AV ben je volledig afhankelijk van wat er in het contract staat of van de wettelijke standaardregels.',
  },
  {
    question: 'Wat moet er in algemene voorwaarden staan?',
    answer: 'Goede algemene voorwaarden voor freelancers bevatten: betalingstermijnen en incassobeleid, aansprakelijkheidsbeperking, intellectueel eigendom en gebruiksrechten, klachtenafhandeling en geschillenbeslechting, opzeggingsvoorwaarden, privacybeleid en gegevensbescherming, en toepasselijk recht (Belgisch of Nederlands).',
  },
  {
    question: 'Hoe stel ik algemene voorwaarden op?',
    answer: 'Met de Pactorae Algemene Voorwaarden Generator maak je in 30 seconden professionele AV op maat voor jouw freelance onderneming. Onze generator stelt de juiste vragen over jouw activiteiten en genereert juridisch correcte voorwaarden die direct bruikbaar zijn in België en Nederland.',
  },
  {
    question: 'Zijn gratis algemene voorwaarden templates geldig?',
    answer: 'Ja, gratis algemene voorwaarden templates kunnen juridisch geldig zijn, mits ze correct zijn opgesteld en van toepassing zijn op jouw situatie. Met Pactorae genereer je gratis AV die zijn afgestemd op jouw specifieke freelance activiteiten en voldoen aan de Belgische en Nederlandse wetgeving.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/algemene-voorwaarden-generator',
      url: 'https://pactorae.com/algemene-voorwaarden-generator',
      name: 'Algemene Voorwaarden Generator voor Freelancers | Pactorae',
      description:
        "Gratis algemene voorwaarden generator voor freelancers en ZZP'ers in België en Nederland. Professioneel, juridisch correct en direct inzetbaar.",
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      })),
    },
  ],
};

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-[#2F4E6F] pr-4">{faq?.question}</span>
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200"
          style={{ backgroundColor: '#FF8C00', transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{faq?.answer}</p>
        </div>
      )}
    </div>
  );
};

const AlgemeneVoorwaardenPreview = () => (
  <div className="relative">
    <div
      className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      style={{ maxWidth: '720px', margin: '0 auto' }}
    >
      <div style={{ padding: '40px 48px 0 48px', fontFamily: 'Georgia, serif', color: '#1a1a1a', lineHeight: '1.8', fontSize: '14px' }}>
        <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', letterSpacing: '0.08em', marginBottom: '4px', textTransform: 'uppercase' }}>ALGEMENE VOORWAARDEN</p>
        <p style={{ textAlign: 'center', color: '#555', fontSize: '13px', marginBottom: '4px' }}>Thomas Janssen — Freelance Webontwikkelaar</p>
        <p style={{ textAlign: 'center', color: '#555', fontSize: '13px', marginBottom: '32px' }}>Opgesteld via Pactorae — pactorae.com</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 1 — TOEPASSELIJKHEID</p>
        <p style={{ marginBottom: '16px' }}>Deze algemene voorwaarden zijn van toepassing op alle aanbiedingen, offertes en overeenkomsten van Thomas Janssen, gevestigd te Mechelsesteenweg 88, 2018 Antwerpen, BTW BE0456.789.123, hierna aangeduid als "Opdrachtnemer".</p>
        <p style={{ marginBottom: '24px' }}>Door het verstrekken van een opdracht aanvaardt de Opdrachtgever de toepasselijkheid van deze algemene voorwaarden. Afwijkingen zijn slechts geldig indien schriftelijk overeengekomen.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 2 — OFFERTES EN TOTSTANDKOMING</p>
        <p style={{ marginBottom: '16px' }}>Alle offertes van Opdrachtnemer zijn vrijblijvend en geldig gedurende <strong>30 dagen</strong> na dagtekening, tenzij anders vermeld. Een overeenkomst komt tot stand door schriftelijke bevestiging van de Opdrachtgever of door aanvang van de werkzaamheden door Opdrachtnemer.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 3 — BETALINGSVOORWAARDEN</p>
        <p style={{ marginBottom: '8px' }}>Facturen dienen te worden voldaan binnen <strong>30 dagen</strong> na factuurdatum. Bij overschrijding van de betalingstermijn is de Opdrachtgever van rechtswege in verzuim en is een vertragingsrente verschuldigd van <strong>1% per maand</strong> over het openstaande bedrag.</p>
        <p style={{ marginBottom: '16px' }}>Opdrachtnemer behoudt het recht om bij wanbetaling de werkzaamheden op te schorten totdat het verschuldigde bedrag volledig is voldaan. Alle buitengerechtelijke incassokosten zijn voor rekening van de Opdrachtgever.</p>
        <p style={{ marginBottom: '24px' }}>Bij opdrachten met een waarde boven € 2.500,00 is Opdrachtnemer gerechtigd een voorschot van 30% te factureren bij aanvang van de werkzaamheden.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 4 — AANSPRAKELIJKHEID</p>
        <p style={{ marginBottom: '16px' }}>De aansprakelijkheid van Opdrachtnemer is beperkt tot het bedrag dat in het kader van de betreffende opdracht in rekening is gebracht, met een maximum van <strong>€ 5.000,00 (vijfduizend euro)</strong>. Opdrachtnemer is niet aansprakelijk voor indirecte schade, gevolgschade, gederfde winst of schade door verlies van gegevens.</p>
        <p style={{ marginBottom: '24px' }}>Opdrachtnemer is niet aansprakelijk voor schade die het gevolg is van onjuiste of onvolledige informatie verstrekt door de Opdrachtgever.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 5 — INTELLECTUEEL EIGENDOM</p>
        <p style={{ marginBottom: '16px' }}>Alle door Opdrachtnemer ontwikkelde werken, inclusief broncode, ontwerpen en documentatie, blijven eigendom van Opdrachtnemer totdat de volledige vergoeding is voldaan. Na volledige betaling worden de intellectuele eigendomsrechten overgedragen aan de Opdrachtgever, tenzij schriftelijk anders overeengekomen.</p>
      </div>

      {/* Fade-out gradient overlay */}
      <div
        style={{
          height: '160px',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,1) 100%)',
          marginTop: '-80px',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* Below fade message */}
      <div style={{ padding: '0 48px 32px 48px', textAlign: 'center' }}>
        <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', marginBottom: '20px' }}>
          Volledig contract genereren met de Pactorae generator
        </p>
        <Link
          to="/contract-generator"
          className="inline-block font-semibold px-8 py-3 rounded-lg text-white transition-colors duration-200 shadow-md hover:shadow-lg"
          style={{ backgroundColor: '#FF8C00' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e07b00'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FF8C00'}
        >
          Genereer jouw contract gratis
        </Link>
      </div>
    </div>
  </div>
);

const AlgemeneVoorwaardenGeneratorPage = () => {
  return (
    <>
      <Helmet>
        <html lang="nl" />
        <title>Algemene Voorwaarden Generator voor Freelancers België &amp; Nederland | Pactorae</title>
        <meta
          name="description"
          content="Gratis algemene voorwaarden generator voor freelancers en zzp'ers in België en Nederland. Professionele AV op maat voor zelfstandigen. Juridisch correct, direct inzetbaar."
        />
        <meta
          name="keywords"
          content="algemene voorwaarden generator België, algemene voorwaarden generator Nederland, algemene voorwaarden zzp, AV freelancer België, AV freelancer Nederland, algemene voorwaarden zelfstandige"
        />
        <link rel="canonical" href="https://pactorae.com/algemene-voorwaarden-generator" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pactorae.com/algemene-voorwaarden-generator" />
        <meta property="og:title" content="Algemene Voorwaarden Generator voor Freelancers België &amp; Nederland | Pactorae" />
        <meta
          property="og:description"
          content="Gratis algemene voorwaarden generator voor freelancers en zzp'ers in België en Nederland. Professionele AV op maat."
        />
        <meta property="og:site_name" content="Pactorae" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Algemene Voorwaarden Generator voor Freelancers België &amp; Nederland | Pactorae" />
        <meta
          name="twitter:description"
          content="Gratis algemene voorwaarden generator voor freelancers en zzp'ers in België en Nederland."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#FF8C00]/20 text-[#FF8C00] text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-[#FF8C00]/30">
              Gratis Generator
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Algemene Voorwaarden Generator
              <span className="block text-[#FF8C00] mt-2">België &amp; Nederland</span>
            </h1>
            <p className="text-xl text-blue-100 mb-4 max-w-2xl mx-auto">
              Genereer in 30 seconden professionele algemene voorwaarden op maat voor jouw freelance onderneming. Juridisch correct voor zelfstandigen in België en zzp'ers in Nederland.
            </p>
            <p className="text-sm text-blue-200 mb-8 max-w-xl mx-auto">
              Geschikt voor zelfstandigen in België, zzp'ers in Nederland en alle freelance professionals.
            </p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Intro */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Wat zijn algemene voorwaarden voor freelancers in België en Nederland?</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Algemene voorwaarden (AV) zijn standaardbepalingen die van toepassing zijn op al jouw opdrachten als freelancer of zzp'er. Of je nu als zelfstandige in België of als zzp'er in Nederland werkt — professionele AV beschermen je juridisch en tonen aan dat je serieus ondernemer bent. Onze generator maakt AV op maat voor zowel het Belgisch als het Nederlands recht.
            </p>
          </section>

          {/* What's included */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Wat bevatten deze algemene voorwaarden?</h2>
            <ul className="space-y-3">
              {[
                { title: 'Betalingstermijnen', desc: 'Standaard betalingstermijnen en incassobeleid bij wanbetaling' },
                { title: 'Aansprakelijkheidsbeperking', desc: 'Beperking van jouw aansprakelijkheid als freelancer' },
                { title: 'Intellectueel eigendom', desc: 'Eigendomsrechten op geleverd werk en gebruikslicenties' },
                { title: 'Klachtenafhandeling', desc: 'Procedure voor het indienen en afhandelen van klachten' },
                { title: 'Opzeggingsvoorwaarden', desc: 'Opzegtermijnen en vergoeding bij vroegtijdige beëindiging' },
                { title: 'Privacybeleid', desc: 'Omgang met persoonsgegevens conform AVG/GDPR' },
                { title: 'Overmacht', desc: 'Regeling bij onvoorziene omstandigheden buiten jouw controle' },
                { title: 'Toepasselijk recht', desc: 'Belgisch of Nederlands recht en bevoegde rechtbank' },
              ]?.map((item, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#FF8C00] rounded-full flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <span className="font-semibold text-[#2F4E6F]">{item?.title}:</span>{' '}
                    <span className="text-gray-600">{item?.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* AV Preview */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Voorbeeld algemene voorwaarden</h2>
            <AlgemeneVoorwaardenPreview />
          </section>

          {/* Internal links */}
          <section className="mb-12">
            <h2 className="text-xl font-bold text-[#2F4E6F] mb-4">Gerelateerde contract templates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { to: '/freelance-contract-template', label: 'Freelance Contract Template' },
                { to: '/nda-template', label: 'NDA Template voor Freelancers' },
                { to: '/consultancy-contract-template', label: 'Consultancy Contract Template' },
                { to: '/freelance-contract-voorbeeld', label: 'Freelance Contract Voorbeeld' },
              ]?.map((link) => (
                <Link
                  key={link?.to}
                  to={link?.to}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors text-[#2F4E6F] font-medium text-sm"
                >
                  <span style={{ color: '#FF8C00' }}>→</span> {link?.label}
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-8">Veelgestelde vragen over algemene voorwaarden</h2>
            <div className="space-y-3">
              {faqs?.map((faq, i) => (
                <FaqItem key={i} faq={faq} />
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] rounded-2xl p-12">
            <h2 className="text-2xl font-bold text-white mb-4">Klaar om jouw algemene voorwaarden te genereren?</h2>
            <p className="text-blue-100 mb-8">Maak in 30 seconden professionele AV op maat voor jouw freelance onderneming.</p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </section>
        </article>
      </main>
    </>
  );
};

export default AlgemeneVoorwaardenGeneratorPage;
